import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext.js';
import { entityService } from '../services/entityService.js';
import { servicesService } from '../services/servicesService.js';
import { clinicService } from '../services/clinicService.js';
import LoadingModal from './LoadingModal.js';
import './Settings.css';

function Settings({ activeTab: initialTab = 'insurances' }) {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [entity, setEntity] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [imageCache, setImageCache] = useState({});
  
  // Estados para planos de saúde
  const [insurances, setInsurances] = useState([]);
  const [selectedInsurances, setSelectedInsurances] = useState([]);
  const [insuranceAdditionalData, setInsuranceAdditionalData] = useState({});
  const [savingInsurances, setSavingInsurances] = useState(false);
  
  // Estados para serviços
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceDescriptions, setServiceDescriptions] = useState({});
  const [savingServices, setSavingServices] = useState(false);

  // Estados para configurações da clínica (v3)
  const DAYS_PT = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
  const EMPTY_BUSINESS_HOURS = DAYS_PT.map((day) => ({ day, open: '08:00', close: '18:00', closed: day === 'Domingo' }));
  const [clinicSettings, setClinicSettings] = useState(null);
  const [clinicForm, setClinicForm] = useState({
    defaultAppointmentDuration: 30,
    specialties: [],
    businessHours: EMPTY_BUSINESS_HOURS,
    prescriptionTemplate: '',
    signatureUrl: '',
  });
  const [newSpecialty, setNewSpecialty] = useState('');
  const [savingClinic, setSavingClinic] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Função para pré-carregar imagens
  const preloadImage = (url) => {
    if (!url || imageCache[url]) return;
    
    const img = new Image();
    img.src = url;
    img.onload = () => {
      setImageCache(prev => ({ ...prev, [url]: true }));
    };
  };

  // Pré-carregar imagens quando os dados são carregados
  useEffect(() => {
    insurances.forEach(ins => {
      if (ins.icon) preloadImage(ins.icon);
    });
  }, [insurances, imageCache]);

  useEffect(() => {
    services.forEach(serv => {
      if (serv.icon) preloadImage(serv.icon);
    });
  }, [services, imageCache]);

  // Verificar se deve mostrar badge de alerta
  const showServiceAlert = selectedServices.length === 0;

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Carregando configurações...');

      // Buscar dados da entidade
      const entityResult = await entityService.getMyEntities();
      if (entityResult.success && entityResult.entities?.length > 0) {
        const entityData = entityResult.entities[0];
        setEntity(entityData);

        // Buscar planos de saúde disponíveis
        const insurancesResult = await entityService.getInsurances();
        if (insurancesResult.success) {
          const activeInsurances = insurancesResult.insurances.filter(ins => ins.active);
          
          // Ordenar para colocar "Outros" por último
          const sortedInsurances = activeInsurances.sort((a, b) => {
            const aIsOther = a.insurance?.toLowerCase().includes('outro');
            const bIsOther = b.insurance?.toLowerCase().includes('outro');
            if (aIsOther && !bIsOther) return 1;
            if (!aIsOther && bIsOther) return -1;
            return 0;
          });
          
          setInsurances(sortedInsurances);
          
          // Definir planos selecionados e additionalData
          if (entityData.insurances && entityData.insurances.length > 0) {
            const selected = entityData.insurances
              .filter(ins => ins.active)
              .map(ins => ins.insuranceId);
            setSelectedInsurances(selected);
            
            const additionalDataMap = {};
            entityData.insurances.forEach(ins => {
              if (ins.active && ins.additionalData) {
                additionalDataMap[ins.insuranceId] = ins.additionalData;
              }
            });
            setInsuranceAdditionalData(additionalDataMap);
          }
        }

        // Buscar serviços disponíveis
        const servicesResult = await servicesService.getServices();
        if (servicesResult.success) {
          const activeServices = servicesResult.services.filter(serv => serv.active);
          setServices(activeServices);
          
          // Definir serviços selecionados e descrições
          if (entityData.services && entityData.services.length > 0) {
            const selected = entityData.services
              .filter(serv => serv.active)
              .map(serv => serv.serviceId);
            setSelectedServices(selected);
            
            const descriptions = {};
            entityData.services.forEach(serv => {
              if (serv.active && serv.description) {
                descriptions[serv.serviceId] = serv.description;
              }
            });
            setServiceDescriptions(descriptions);
          }
        }

        // Buscar configurações da clínica (v3)
        const clinicResult = await clinicService.getSettings(entityData.id);
        if (clinicResult.success && clinicResult.settings) {
          const s = clinicResult.settings;
          setClinicSettings(s);
          setClinicForm((prev) => ({
            ...prev,
            defaultAppointmentDuration: s.defaultAppointmentDuration || 30,
            specialties: s.specialties || [],
            businessHours: s.businessHours?.length ? s.businessHours : prev.businessHours,
            prescriptionTemplate: s.prescriptionTemplate || '',
            signatureUrl: s.signatureUrl || '',
          }));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleSaveClinicSettings = async () => {
    try {
      setSavingClinic(true);
      setLoadingMessage('Salvando configurações da clínica...');
      const result = await clinicService.updateSettings(entity.id, clinicForm);
      if (result.success) {
        toast.success('Configurações da clínica atualizadas!');
      } else {
        toast.error(result.error || 'Erro ao salvar configurações');
      }
    } catch (error) {
      console.error('Erro ao salvar configurações da clínica:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingClinic(false);
      setLoadingMessage('');
    }
  };

  const handleAddSpecialty = () => {
    const s = newSpecialty.trim();
    if (!s) return;
    if (clinicForm.specialties.includes(s)) { toast.error('Especialidade já adicionada'); return; }
    setClinicForm((prev) => ({ ...prev, specialties: [...prev.specialties, s] }));
    setNewSpecialty('');
  };

  const handleRemoveSpecialty = (s) => {
    setClinicForm((prev) => ({ ...prev, specialties: prev.specialties.filter((x) => x !== s) }));
  };

  const handleBusinessHoursChange = (index, field, value) => {
    setClinicForm((prev) => {
      const bh = [...prev.businessHours];
      bh[index] = { ...bh[index], [field]: value };
      return { ...prev, businessHours: bh };
    });
  };

  const toggleInsurance = (insuranceId) => {
    setSelectedInsurances(prev => {
      if (prev.includes(insuranceId)) {
        // Remove também o additionalData se existir
        const newAdditionalData = { ...insuranceAdditionalData };
        delete newAdditionalData[insuranceId];
        setInsuranceAdditionalData(newAdditionalData);
        return prev.filter(id => id !== insuranceId);
      } else {
        return [...prev, insuranceId];
      }
    });
  };

  const handleInsuranceAdditionalDataChange = (insuranceId, data) => {
    setInsuranceAdditionalData(prev => ({
      ...prev,
      [insuranceId]: data
    }));
  };

  const toggleService = (serviceId) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceId)) {
        // Remove também a descrição se existir
        const newDescriptions = { ...serviceDescriptions };
        delete newDescriptions[serviceId];
        setServiceDescriptions(newDescriptions);
        return prev.filter(id => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  const handleServiceDescriptionChange = (serviceId, description) => {
    setServiceDescriptions(prev => ({
      ...prev,
      [serviceId]: description
    }));
  };

  const handleSaveInsurances = async () => {
    try {
      setSavingInsurances(true);
      setLoadingMessage('Salvando planos de saúde...');

      // Preparar dados com additionalData
      const insurancesData = selectedInsurances.map(insuranceId => ({
        insuranceId,
        additionalData: insuranceAdditionalData[insuranceId] || null
      }));

      await entityService.updateInsurancesWithData(entity.id, insurancesData);
      
      toast.success('Planos de saúde atualizados com sucesso!');
      
      // Atualizar dados do usuário
      await refreshUser();
      
      // Recarregar dados
      await loadData();
    } catch (error) {
      console.error('Erro ao salvar planos:', error);
      toast.error(error.message || 'Erro ao salvar planos de saúde');
    } finally {
      setSavingInsurances(false);
      setLoadingMessage('');
    }
  };

  const handleSaveServices = async () => {
    try {
      setSavingServices(true);
      setLoadingMessage('Salvando serviços...');

      const servicesData = selectedServices.map(serviceId => ({
        serviceId,
        description: serviceDescriptions[serviceId] || null
      }));

      await servicesService.updateEntityServices(entity.id, servicesData);
      
      toast.success('Serviços atualizados com sucesso!');
      
      // Atualizar dados do usuário
      await refreshUser();
      
      // Recarregar dados
      await loadData();
    } catch (error) {
      console.error('Erro ao salvar serviços:', error);
      toast.error(error.message || 'Erro ao salvar serviços');
    } finally {
      setSavingServices(false);
      setLoadingMessage('');
    }
  };

  if (loading) {
    return <LoadingModal message={loadingMessage} />;
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1 className="settings-title">Configurações</h1>
        <p className="settings-subtitle">
          Configure planos de saúde e serviços oferecidos
        </p>
      </div>

      {/* Tabs */}
      <div className="settings-tabs">
        <button
          className={`tab-button ${activeTab === 'insurances' ? 'active' : ''}`}
          onClick={() => setActiveTab('insurances')}
        >
          Planos de Saúde
        </button>
        <button
          className={`tab-button ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
          style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          Serviços Oferecidos
          {showServiceAlert && (
            <span
              className={`settings-service-alert-badge ${activeTab === 'services' ? 'active' : ''}`}
              title="Adicione ao menos 1 serviço"
            >
              1
            </span>
          )}
        </button>
        {/* <button
          className={`tab-button ${activeTab === 'clinic' ? 'active' : ''}`}
          onClick={() => setActiveTab('clinic')}
        >
          Clínica
        </button> */}
      </div>

      {/* Conteúdo das Abas */}
      <div className="tab-content" key={activeTab}>
        {activeTab === 'insurances' ? (
          <div className="settings-section">
            <div className="section-header">
              <div className="section-header-content">
                <h2 className="section-title-settings">Planos de Saúde</h2>
                <p className="section-description">
                  Selecione os planos que você aceita e adicione informações adicionais se necessário
                </p>
              </div>
            </div>

            <div className="items-grid">
              {insurances.map(insurance => {
                const isSelected = selectedInsurances.includes(insurance.id);
                const isOther = insurance.insurance?.toLowerCase().includes('outro');
                return (
                  <div
                    key={insurance.id}
                    className={`item-card-wrapper ${isSelected && isOther ? 'has-additional' : ''}`}
                  >
                    <div
                      className={`item-card-horizontal ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleInsurance(insurance.id)}
                    >
                      <div className="item-checkbox">
                        {isSelected && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </div>
                      {insurance.icon && (
                        <div className="item-icon-horizontal">
                          <img 
                            src={insurance.icon} 
                            alt={insurance.insurance}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      )}
                      <div className="item-content">
                        <span className="item-name">{insurance.insurance}</span>
                        {insurance.description && (
                          <span className="item-description">{insurance.description}</span>
                        )}
                      </div>
                    </div>
                    {isSelected && isOther && (
                      <div className="additional-data-field">
                        <input
                          type="text"
                          className="additional-data-input"
                          placeholder="Informe o nome do plano"
                          value={insuranceAdditionalData[insurance.id] || ''}
                          onChange={(e) => handleInsuranceAdditionalDataChange(insurance.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="section-actions">
              <button 
                className="btn-save"
                onClick={handleSaveInsurances}
                disabled={savingInsurances}
              >
                {savingInsurances ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        ) : activeTab === 'services' ? (
          <div className="settings-section">
            <div className="section-header">
              <div className="section-header-content">
                <h2 className="section-title-settings">Serviços Oferecidos</h2>
                <p className="section-description">
                  Selecione os serviços que você oferece e adicione descrições opcionais
                </p>
              </div>
            </div>

            <div className="services-list">
              {services.map(service => {
                const isSelected = selectedServices.includes(service.id);
                return (
                  <div
                    key={service.id}
                    className={`service-item-horizontal ${isSelected ? 'selected' : ''}`}
                  >
                    <div 
                      className="service-header-horizontal"
                      onClick={() => toggleService(service.id)}
                    >
                      <div className="service-info-horizontal">
                        <div className="item-checkbox">
                          {isSelected && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </div>
                        {service.icon && (
                          <div className="service-icon-horizontal">
                            <img 
                              src={service.icon} 
                              alt={service.name}
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        )}
                        <div className="item-content">
                          <span className="item-name">{service.name}</span>
                          {service.description && !isSelected && (
                            <span className="item-description">{service.description}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {isSelected && (
                      <div className="service-description-area">
                        <textarea
                          className="description-input"
                          placeholder="Adicione uma descrição para este serviço (opcional)"
                          value={serviceDescriptions[service.id] || ''}
                          onChange={(e) => handleServiceDescriptionChange(service.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          rows={3}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="section-actions">
              <button
                className="btn-save"
                onClick={handleSaveServices}
                disabled={savingServices}
              >
                {savingServices ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        ) : activeTab === 'clinic' ? (
          /* ── Clínica (v3) ──────────────────────────────────── */
          <div className="settings-section">
            <div className="section-header">
              <div className="section-header-content">
                <h2 className="section-title-settings">Configurações da Clínica</h2>
                <p className="section-description">Personalize os padrões de atendimento e horários de funcionamento</p>
              </div>
            </div>

            {/* Duração padrão */}
            <div style={{ marginBottom: '24px' }}>
              <label className="form-label" style={{ fontSize: '13px', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>
                Duração Padrão da Consulta (minutos)
              </label>
              <input
                type="number"
                className="form-input"
                style={{ maxWidth: '200px', padding: '10px 14px', border: '1.5px solid #E5E5E5', borderRadius: '10px', fontSize: '14px' }}
                value={clinicForm.defaultAppointmentDuration}
                onChange={(e) => setClinicForm((p) => ({ ...p, defaultAppointmentDuration: Number(e.target.value) }))}
                min="5"
                step="5"
              />
            </div>

            {/* Especialidades */}
            <div style={{ marginBottom: '24px' }}>
              <label className="form-label" style={{ fontSize: '13px', fontWeight: '600', color: '#666', display: 'block', marginBottom: '8px' }}>Especialidades</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #E5E5E5', borderRadius: '10px', fontSize: '14px' }}
                  placeholder="Ex: Dermatologia, Oncologia..."
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSpecialty(); } }}
                />
                <button onClick={handleAddSpecialty} style={{ padding: '10px 20px', background: 'var(--color-main, #7165FF)', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}>
                  Adicionar
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {clinicForm.specialties.map((s) => (
                  <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(113,101,255,0.1)', color: 'var(--color-main, #7165FF)', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '500' }}>
                    {s}
                    <button onClick={() => handleRemoveSpecialty(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 0, color: 'inherit' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Horários de funcionamento */}
            <div style={{ marginBottom: '24px' }}>
              <label className="form-label" style={{ fontSize: '13px', fontWeight: '600', color: '#666', display: 'block', marginBottom: '12px' }}>Horários de Funcionamento</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {clinicForm.businessHours.map((bh, i) => (
                  <div key={bh.day} style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span style={{ width: '80px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>{bh.day}</span>
                    {bh.closed ? (
                      <span style={{ fontSize: '13px', color: '#999' }}>—— fechado ——</span>
                    ) : (
                      <>
                        <input type="time" value={bh.open} onChange={(e) => handleBusinessHoursChange(i, 'open', e.target.value)} style={{ padding: '6px 10px', border: '1.5px solid #E5E5E5', borderRadius: '8px', fontSize: '13px' }} />
                        <span style={{ fontSize: '13px', color: '#999' }}>até</span>
                        <input type="time" value={bh.close} onChange={(e) => handleBusinessHoursChange(i, 'close', e.target.value)} style={{ padding: '6px 10px', border: '1.5px solid #E5E5E5', borderRadius: '8px', fontSize: '13px' }} />
                      </>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#666', cursor: 'pointer', marginLeft: 'auto' }}>
                      <input type="checkbox" checked={bh.closed} onChange={(e) => handleBusinessHoursChange(i, 'closed', e.target.checked)} style={{ cursor: 'pointer' }} />
                      Fechado
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Template de receituário */}
            <div style={{ marginBottom: '24px' }}>
              <label className="form-label" style={{ fontSize: '13px', fontWeight: '600', color: '#666', display: 'block', marginBottom: '6px' }}>Template de Receituário</label>
              <textarea
                className="form-textarea"
                style={{ width: '100%', minHeight: '120px', padding: '12px 14px', border: '1.5px solid #E5E5E5', borderRadius: '10px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
                value={clinicForm.prescriptionTemplate}
                onChange={(e) => setClinicForm((p) => ({ ...p, prescriptionTemplate: e.target.value }))}
                placeholder="Cabeçalho padrão das receitas..."
              />
            </div>

            <div className="section-actions">
              <button
                className="btn-save"
                onClick={handleSaveClinicSettings}
                disabled={savingClinic}
              >
                {savingClinic ? 'Salvando...' : 'Salvar Configurações'}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {loadingMessage && <LoadingModal message={loadingMessage} />}
    </div>
  );
}

export default Settings;
